import React, { useState, useMemo } from "react";
import { UPDATE_DOES_NO_APPLY } from "../../../api/apiDoesNotApply";
import Button from "../../atoms/Button";

const ModalBlockedTypes = ({ doesNotApplyRecord, empresa, onClose, onSaved, allowFiles }) => {
    // blocks: array de {id, name, tableName} actualmente bloqueados
    const initialBlocked = doesNotApplyRecord?.blocks || [];
    const [blocked, setBlocked] = useState(initialBlocked.map((b) => b.id));
    const [saving, setSaving] = useState(false);

    // Agrupar types por tableName para organizar visualmente
    const grouped = useMemo(() => {
        if (!allowFiles || !Array.isArray(allowFiles)) return {};
        return allowFiles.reduce((acc, item) => {
            const key = item.tableName || "otros";
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
        }, {});
    }, [allowFiles]);

    const toggleBlock = (item) => {
        setBlocked((prev) =>
            prev.includes(item.id)
                ? prev.filter((id) => id !== item.id)
                : [...prev, item.id]
        );
    };

    // Seleccionar/deseleccionar todos de un grupo
    const toggleGroup = (items) => {
        const allBlocked = items.every((item) => blocked.includes(item.id));
        if (allBlocked) {
            // Deseleccionar todos del grupo
            setBlocked((prev) => prev.filter((id) => !items.some((item) => item.id === id)));
        } else {
            // Seleccionar todos del grupo
            setBlocked((prev) => {
                const newIds = items.map((item) => item.id).filter((id) => !prev.includes(id));
                return [...prev, ...newIds];
            });
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            
            // Conservar los bloques de otras tablas que no están presentes en allowFiles
            const otherTablesBlocks = initialBlocked.filter(
                (b) => !allowFiles.some((af) => af.id === b.id)
            );

            // Obtener los bloques actualizados para la tabla actual
            const currentTableBlocks = allowFiles
                .filter((item) => blocked.includes(item.id))
                .map(({ id, name, tableName }) => ({ id, name, tableName }));

            const newBlocks = [...otherTablesBlocks, ...currentTableBlocks];

            await UPDATE_DOES_NO_APPLY(doesNotApplyRecord.id, {
                clientId: empresa.id,
                blocks: newBlocks,
            });
            onSaved();
            onClose();
        } catch (error) {
            console.error("Error al guardar bloques:", error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <p className="text-sm text-slate-500">
                Marca los tipos de documento que <strong>NO aplican</strong> para esta empresa. No se mostrarán en la lista de documentos.
            </p>

            {Object.entries(grouped).map(([tableName, items]) => {
                const allBlocked = items.every((item) => blocked.includes(item.id));
                const someBlocked = items.some((item) => blocked.includes(item.id));

                return (
                    <div key={tableName} className="border border-slate-200 rounded-lg overflow-hidden">
                        {/* Header del grupo con toggle para seleccionar todos */}
                        <label
                            className={`flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors
                                ${allBlocked ? "bg-red-100" : "bg-slate-100"}`}
                        >
                            <input
                                type="checkbox"
                                checked={allBlocked}
                                ref={(el) => {
                                    if (el) el.indeterminate = someBlocked && !allBlocked;
                                }}
                                onChange={() => toggleGroup(items)}
                                className="accent-red-500 w-4 h-4 cursor-pointer"
                            />
                            <p className="text-xs font-bold uppercase tracking-widest text-slate-600">
                                {tableName.replace(/_/g, " ")}
                            </p>
                            <span className="ml-auto text-xs text-slate-400">
                                {items.filter((i) => blocked.includes(i.id)).length}/{items.length} bloqueados
                            </span>
                        </label>

                        {/* Items del grupo */}
                        <div className="divide-y divide-slate-100">
                            {items.map((item) => {
                                const isBlocked = blocked.includes(item.id);
                                return (
                                    <label
                                        key={item.id}
                                        className={`flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors
                                            ${isBlocked ? "bg-red-50" : "hover:bg-slate-50"}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isBlocked}
                                            onChange={() => toggleBlock(item)}
                                            className="accent-red-500 w-4 h-4 cursor-pointer"
                                        />
                                        <span className={`text-sm ${isBlocked ? "text-red-600 line-through" : "text-slate-700"}`}>
                                            {item.name}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
                <Button text="Cancelar" bgColor="red" onClick={onClose} />
                <Button text={saving ? "Guardando..." : "Guardar cambios"} bgColor="green" onClick={handleSave} disabled={saving} />
            </div>
        </div>
    );
};

export default ModalBlockedTypes;
